import {
	AdvancedMarker,
	ControlPosition,
	Map,
	MapControl,
	useMap,
	APIProvider,
} from '@vis.gl/react-google-maps'
import React, { useEffect, useRef } from 'react'

const DENMARK_LOCATION = { lat: 55.48867978959782, lng: 10.237095943830647 }

interface Location {
	lat: number
	lng: number
	storeName: string
	storeId: string
	storeChannelKey: string
}

interface GoogleMapStoresV2ComponentProps {
	locations: Location[]
	zoom: number
	defaultCenter?: { lat: number; lng: number }
	selectedStoreChannelKey: string[]
	onMarkerClick: (
		storeChannelKey: string,
		selectedStoreChannelKey: string[],
	) => void
	height?: string
}

const GoogleMapStoresV2Component: React.FC<GoogleMapStoresV2ComponentProps> = ({
	...props
}) => {
	return (
		<APIProvider apiKey={ENV.GOOGLE_MAPS_API_KEY as string}>
			<GoogleMapStoresComponent {...props} />
		</APIProvider>
	)
}

const GoogleMapStoresComponent: React.FC<GoogleMapStoresV2ComponentProps> = ({
	locations,
	zoom,
	defaultCenter,
	selectedStoreChannelKey,
	onMarkerClick,
	height,
}) => {
	const map = useMap()
	const selectedStoresRef = useRef<string[]>(selectedStoreChannelKey || [])
	const containerStyle = {
		width: '100%',
		maxHeight: '400px',
	}

	// if there is a new selected store, center the map to the new store
	useEffect(() => {
		const selectedStoreChannel = selectedStoreChannelKey.find(
			selected => selectedStoresRef.current.indexOf(selected) === -1,
		)
		const selectedLocation = locations.find(
			loc => loc.storeChannelKey === selectedStoreChannel,
		)
		if (map && selectedLocation) {
			map.setZoom(13)
			map.setCenter({ lat: selectedLocation.lat, lng: selectedLocation.lng })
		}
		selectedStoresRef.current = selectedStoreChannelKey
	}, [locations, map, selectedStoreChannelKey])

	const handleOnClick = (storeChannelKey: string, location: Location) => {
		if (map) {
			map.setZoom(15)
			map.setCenter({ lat: location.lat, lng: location.lng })
		}
		onMarkerClick(storeChannelKey, selectedStoreChannelKey || [])
	}

	return (
		<Map
			style={containerStyle}
			defaultCenter={defaultCenter ? defaultCenter : DENMARK_LOCATION}
			defaultZoom={zoom}
			gestureHandling={'greedy'}
			disableDefaultUI={true}
			mapId={(ENV.GOOGLE_MAPS_ID as string) || 'map'}
		>
			<MapControl position={ControlPosition.BOTTOM} />
			{locations.map((location: Location) => {
				return (
					<AdvancedMarker
						key={location.storeChannelKey}
						position={{ lat: location.lat, lng: location.lng }}
						title={location.storeName}
						onClick={() => {
							handleOnClick(location.storeChannelKey, location)
						}}
					>
						<div className=" h-20 w-20 cursor-pointer bg-contain bg-center bg-no-repeat">
							<img
								src={getPinSrc(
									location.storeId,
									selectedStoreChannelKey.includes(location.storeChannelKey),
								)}
								className="h-20 w-20"
								alt="store-pin"
							/>
						</div>
					</AdvancedMarker>
				)
			})}
		</Map>
	)
}

function getPinSrc(storeName: string, active: boolean) {
	if (storeName.includes('pieces')) {
		return active
			? '/img/map-pins/pieces-active.svg'
			: '/img/map-pins/pieces.svg'
	} else if (storeName.includes('selected')) {
		return active
			? '/img/map-pins/selected-active.svg'
			: '/img/map-pins/selected.svg'
	} else if (storeName.includes('bestseller')) {
		return active
			? '/img/map-pins/bestseller-active.svg'
			: '/img/map-pins/bestseller.svg'
	} else if (storeName.includes('vero_moda')) {
		return active
			? '/img/map-pins/vero-moda-active.svg'
			: '/img/map-pins/vero-moda.svg'
	} else if (storeName.includes('name_it')) {
		return active
			? '/img/map-pins/name-it-active.svg'
			: '/img/map-pins/name-it.svg'
	} else {
		return '/img/map-pins/default.svg'
	}
}

export default GoogleMapStoresV2Component
